import type {
  ClerkUser,
  PageAdminEvaluationsData,
  PageAdminUsersFilter,
} from '@agentselly/immodossier-shared';
import {
  ActionSchema,
  AppAction,
  pageAdminUsersFilterSchema,
  QueryReference,
} from '@agentselly/immodossier-shared';
import { useNavigate } from '@tanstack/react-router';
import { useBackendQuery } from '~/helper/useBackendQuery';
import { ErrorWidget } from '~/components/widget/base/ErrorWidget';
import { LoadingWidget } from '~/components/widget/base/LoadingWidget';
import { formatDateTime, WidgetTitle, WidgetWrapper } from '@agentselly/ui';
import { useTranslate } from '@tolgee/react';
import { clsx } from 'clsx';
import { useEffect, useState } from 'react';
import ReactJson from 'react-json-view';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useQueryClient } from '@tanstack/react-query';
import { fetchBackend } from '~/helper/fetchBackend.ts';

export function LegacyUsersPage(props: {
  readonly search: Record<string, string | string[] | undefined>;
  readonly fullPath: string;
}) {
  const { t } = useTranslate();
  const navigate = useNavigate();

  const rawSearch = props.search;

  const parsedSearch = pageAdminUsersFilterSchema.safeParse(rawSearch);

  const currentFilter: PageAdminUsersFilter = parsedSearch.success
    ? parsedSearch.data
    : {};

  const query = useBackendQuery(QueryReference.PageAdminUsers);

  const [
    selectedUserId,
    setSelectedUserId,
  ] = useState('');

  if (query.isLoading) {
    return <LoadingWidget />;
  }

  if (query.isError) {
    return <ErrorWidget error={query.error} />;
  }

  async function updateFilter(filter: PageAdminUsersFilter) {
    const newFilter = {
      ...currentFilter,
      ...filter,
    };

    const shape = pageAdminUsersFilterSchema._def.shape();

    const keys = Object.keys(shape) as (keyof PageAdminUsersFilter)[];

    for (const key of keys) {
      if (key === 'page') {
        continue;
      }

      const value = newFilter[key];

      if (value === currentFilter[key]) {
        continue;
      }

      if (
        typeof value === 'undefined' ||
        value === null ||
        value === '' ||
        (Array.isArray(value) && !value.length)
      ) {
        continue;
      }

      // Reset page when changing filter
      (newFilter as PageAdminUsersFilter).page = 1;
      break;
    }

    const nextSearch: PageAdminUsersFilter = {};

    for (const key of keys) {
      const value = newFilter[key];

      if (
        typeof value === 'undefined' ||
        value === null ||
        value === '' ||
        (Array.isArray(value) && !value.length)
      ) {
        continue;
      }

      nextSearch[key] = value;
    }

    await navigate({
      to: props.fullPath,
      search: nextSearch as never,
      replace: true,
    } as never);

    await query.refetch();
  }

  const paginationProps: PaginationWidgetProps = {
    pagination: query.data?.pagination,
    resultDate: formatDateTime(query.data?.resultTimestamp),
    updateFilter: updateFilter,
  };

  if (!parsedSearch.success) {
    return <ErrorWidget error={parsedSearch.error} />;
  }

  const selectedUser = selectedUserId
    ? query.data?.users?.find((it) => it.id === selectedUserId)
    : undefined;

  return (
    <div className="flex flex-col gap-8">
      <UserDetailsDialog
        onCancel={() => setSelectedUserId('')}
        canMutate={query.data?.canMutate}
        user={selectedUser}
      />
      <WidgetWrapper>
        <WidgetTitle>{t('admin_evaluations_filter_title')}</WidgetTitle>
        <div className="mt-8 flex flex-row gap-4 flex-wrap">
          <div>
            <label htmlFor="filterOrderBy">Sortieren nach</label>
            <br />
            <select
              id="filterOrderBy"
              onChange={(event) =>
                updateFilter({
                  orderBy: event.target
                    .value as PageAdminUsersFilter['orderBy'],
                })
              }
              defaultValue={currentFilter.orderBy || ''}
            >
              <option value="">Standard</option>
              <option value="created_at">Erstellt am</option>
              <option value="updated_at">Aktualisiert am</option>
              <option value="email_address">E-Mail</option>
              <option value="first_name">Vorname</option>
              <option value="last_name">Nachname</option>
              <option value="phone_number">Telefon</option>
              <option value="username">Benutzername</option>
              <option value="last_active_at">Zuletzt aktiv</option>
              <option value="last_sign_in_at">Zuletzt angemeldet</option>
            </select>
            <br />
            <label htmlFor="filterOrderByAscending">
              <input
                id="filterOrderByAscending"
                type="checkbox"
                onChange={(event) =>
                  updateFilter({
                    orderByAscending: event.target.checked,
                  })
                }
                defaultChecked={currentFilter.orderByAscending}
              />
              <span> Aufsteigend sortieren</span>
            </label>
          </div>
          <div>
            <label htmlFor="filterQuery">
              <span>Suche</span>
            </label>
            <br />
            <input
              id="filterQuery"
              type="text"
              placeholder="Suchen nach"
              onChange={(event) =>
                updateFilter({
                  query: event.target.value,
                })
              }
              defaultValue={currentFilter.query}
            />
          </div>
        </div>
      </WidgetWrapper>
      <PaginationWidget {...paginationProps} />
      <WidgetWrapper>
        <WidgetTitle>{t('admin_evaluations_results_title')}</WidgetTitle>
        <br />
        {query.data?.users?.length ? null : (
          <p>{t('admin_evaluations_results_empty')}</p>
        )}
        <table className="w-full">
          <tbody>
            {query.data?.users?.filter(Boolean)?.map((user) => {
              return (
                <tr
                  key={user.id}
                  className={clsx(
                    'border-t cursor-pointer hover:bg-primary-100',
                    user.banned ? 'bg-red-50' : '',
                    !user.banned && user.locked ? 'bg-yellow-50' : '',
                  )}
                  onClick={() => setSelectedUserId(user.id)}
                >
                  <td>
                    {user.banned ? '🚫' : ''}
                    {user.locked ? '🔒' : ''}
                    {!user.banned && !user.locked ? '✅' : ''}
                  </td>
                  <td className="p-4 min-w-20">
                    <img
                      alt=""
                      src={user.imageUrl}
                      className="w-12 h-12 rounded-full object-cover border-[1px] border-secondary-900 bg-secondary-600"
                    />
                  </td>
                  <td className="p-4 w-px whitespace-nowrap">
                    <ul>
                      {user.emailAddresses?.map((email) => (
                        <li
                          key={email.id}
                          className={clsx(
                            user.primaryEmailAddressId === email.id
                              ? 'font-bold'
                              : '',
                          )}
                        >
                          {email.emailAddress}
                          {email.verification?.status !== 'verified'
                            ? '❌'
                            : null}
                        </li>
                      ))}
                    </ul>
                    {user.username ? (
                      <p className="text-xs">Benutzername: {user.username}</p>
                    ) : null}
                    {user.firstName || user.lastName ? (
                      <p className="text-xs">
                        {user.firstName} {user.lastName}
                      </p>
                    ) : null}
                    {user.id ? <p className="text-xs">ID: {user.id}</p> : null}
                  </td>
                  <td className="p-4 w-px whitespace-nowrap">
                    {user.createdAt ? (
                      <p>
                        <small>Erstellt am</small>
                        <br />
                        <strong>{formatDateTime(user.createdAt)}</strong>
                      </p>
                    ) : null}
                    {user.updatedAt ? (
                      <p>
                        <small>Aktualisiert am</small>
                        <br />
                        <strong>{formatDateTime(user.updatedAt)}</strong>
                      </p>
                    ) : null}
                  </td>
                  <td className="p-4 w-px whitespace-nowrap">
                    {user.lastActiveAt ? (
                      <p>
                        <small>Letzte Aktivität</small>
                        <br />
                        <strong>{formatDateTime(user.lastActiveAt)}</strong>
                      </p>
                    ) : null}
                    {user.lastSignInAt ? (
                      <p>
                        <small>Letztes Login</small>
                        <br />
                        <strong>{formatDateTime(user.lastSignInAt)}</strong>
                      </p>
                    ) : null}
                  </td>
                  <td className="p-4 w-px whitespace-nowrap">
                    {Object.entries(user.publicMetadata || {}).map(
                      ([
                        key,
                        value,
                      ]) => {
                        const displayValue =
                          typeof value === 'object'
                            ? JSON.stringify(value)
                            : String(value);

                        if (key === 'role') {
                          const roleClasses: Record<string, string> = {
                            admin: 'bg-red-200',
                            internal: 'bg-orange-200',
                            partner: 'bg-yellow-200',
                            user: 'bg-green-200',
                          };

                          return (
                            <p key={key}>
                              <strong>{key}</strong>:{' '}
                              <span
                                className={clsx(
                                  'px-1 rounded',
                                  roleClasses[displayValue] ||
                                    'bg-gray-200 dark:bg-gray-700',
                                )}
                              >
                                {displayValue}
                              </span>
                            </p>
                          );
                        }

                        return (
                          <p key={key}>
                            <strong>{key}</strong>: {displayValue}
                          </p>
                        );
                      },
                    )}
                  </td>
                  <td className="w-full">&nbsp;</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </WidgetWrapper>
      <PaginationWidget {...paginationProps} />
    </div>
  );
}

function PaginationWidget(props: PaginationWidgetProps) {
  const { t } = useTranslate();

  const page = props.pagination?.page || 0;
  const pageCount = props.pagination?.pageCount || 0;

  return (
    <WidgetWrapper>
      <div className="flex flex-col lg:flex-row gap-8 lg:justify-between">
        <PaginationButton
          disabled={page <= 1}
          updateFilter={props.updateFilter}
          targetPage={1}
          text={t('admin_evaluations_pagination_first')}
        />
        <PaginationButton
          disabled={page - 1 < 1}
          updateFilter={props.updateFilter}
          targetPage={page - 1}
          text={t('admin_evaluations_pagination_previous')}
        />
        <p className="text-center">
          {t('admin_evaluations_pagination_text', {
            page: page,
            total: pageCount,
          })}
          <br />
          <small>
            {t('admin_evaluations_pagination_results', {
              resultCount: props.pagination?.total || 0,
              resultDate: props.resultDate,
            })}
          </small>
        </p>
        <PaginationButton
          disabled={page + 1 > pageCount}
          updateFilter={props.updateFilter}
          targetPage={page + 1}
          text={t('admin_evaluations_pagination_next')}
        />
        <PaginationButton
          disabled={page >= pageCount}
          updateFilter={props.updateFilter}
          targetPage={pageCount}
          text={t('admin_evaluations_pagination_last')}
        />
      </div>
    </WidgetWrapper>
  );
}

interface PaginationWidgetProps {
  readonly pagination: PageAdminEvaluationsData['pagination'] | undefined;
  readonly resultDate: string;
  readonly updateFilter: (filter: PageAdminUsersFilter) => void;
}

function PaginationButton(props: PaginationButtonProps) {
  return (
    <button
      onClick={() =>
        props.updateFilter({
          page: props.targetPage || undefined,
        })
      }
      disabled={props.disabled}
      className={clsx(
        'px-4 py-2 rounded-md',
        props.disabled
          ? 'bg-gray-200 dark:bg-gray-700'
          : 'bg-secondary-600 text-white',
      )}
    >
      {props.text}
    </button>
  );
}

interface PaginationButtonProps {
  readonly disabled: boolean;
  readonly updateFilter: (filter: PageAdminUsersFilter) => void;
  readonly targetPage: number;
  readonly text: string;
}

function UserDetailsDialog(props: UserDetailsDialogProps) {
  const { t } = useTranslate();

  const user = props.user;

  if (!user) {
    return null;
  }

  return (
    <div className="fixed top-0 right-0 bottom-0 left-0 z-[999999] flex items-center justify-center text-white">
      <div
        className="absolute top-0 right-0 bottom-0 left-0 bg-black opacity-80"
        onClick={() =>
          confirm(t('admin_evaluations_userdetail_closeconfirm')) &&
          props.onCancel()
        }
      ></div>
      <div
        className="relative z-1 p-8 rounded-lg bg-white dark:bg-gray-800 text-gray-950 dark:text-gray-100 min-w-[50svw] max-w-[90svw] min-h-[50svh] max-h-[90svh] overflow-auto"
        onClick={() => false}
      >
        <button
          onClick={() => props.onCancel()}
          className="hover:bg-red-300 absolute top-4 right-4"
        >
          <XMarkIcon className="w-8 h-8" />
        </button>
        <div className="flex flex-col gap-4">
          <h1 className="text-xl">
            {t('admin_evaluations_userdetail_title')}{' '}
            <strong>
              {user.emailAddresses.find(
                (it) => it.id === user.primaryEmailAddressId,
              )?.emailAddress || user.id}
            </strong>
          </h1>
          <p>&nbsp;</p>

          <ul className="list-disc pl-4">
            <li>
              <a
                href={`https://dashboard.clerk.com/apps/app_2qxN29A6IozvGlwUx1mNAjiFtH0/instances/ins_2siVGQt4cvjzblObK1CneZ6KatG/users/${user.id}`}
                target="_blank"
                rel="noreferrer"
                className="text-primary-600 underline"
              >
                {t('admin_evaluations_userdetail_clerklink')}
              </a>
            </li>
            <li>
              <a
                href={`https://app-eu1.hubspot.com/contacts/26776524/record/0-1/${user.publicMetadata?.hubspotContactId}/`}
                target="_blank"
                rel="noreferrer"
                className="text-primary-600 underline"
              >
                {t('admin_evaluations_userdetail_hubspotlink')}
              </a>
            </li>
          </ul>
          <p>&nbsp;</p>

          <h2 className="font-bold">
            {t('admin_evaluations_userdetail_mutationtitle')}
          </h2>
          {props.canMutate ? (
            <UserMutationsForm user={props.user} />
          ) : (
            <p>{t('admin_evaluations_userdetail_noaccess')}</p>
          )}
          <p>&nbsp;</p>

          <h2 className="font-bold">
            {t('admin_evaluations_userdetail_emailstitle')}
          </h2>
          <ul className="list-disc pl-4">
            {user.emailAddresses?.map((email) => (
              <li key={email.id}>
                {email.emailAddress} ({email.verification?.status || 'unknown'})
              </li>
            ))}
            {!user.emailAddresses?.length ? (
              <li>{t('admin_evaluations_userdetail_noemails')}</li>
            ) : null}
          </ul>
          <p>&nbsp;</p>

          <h2 className="font-bold">Telefon Nummern</h2>
          <ul className="list-disc pl-4">
            {user.phoneNumbers?.map((phone) => (
              <li key={phone.id}>
                {phone.phoneNumber} ({phone.verification?.status || 'unknown'})
              </li>
            ))}
            {!user.phoneNumbers?.length ? <li>Keine Telefon Nummern</li> : null}
          </ul>
          <p>&nbsp;</p>

          <h2 className="font-bold">Aktivitäten</h2>
          <ul className="list-disc pl-4">
            <li>
              <strong>Erstellt am</strong>:{' '}
              {formatDateTime(user.createdAt, { fallback: 'unbekannt' })}
            </li>
            <li>
              <strong>Aktualisiert am</strong>:{' '}
              {formatDateTime(user.updatedAt, { fallback: 'unbekannt' })}
            </li>
            <li>
              <strong>Zuletzt aktiv</strong>:{' '}
              {formatDateTime(user.lastActiveAt, { fallback: 'unbekannt' })}
            </li>
            <li>
              <strong>Zuletzt angemeldet</strong>:{' '}
              {formatDateTime(user.lastSignInAt, { fallback: 'unbekannt' })}
            </li>
          </ul>
          <p>&nbsp;</p>

          <h2 className="font-bold">DEBUG (Dev-Tools)</h2>
          <div className="whitespace-pre font-mono text-black dark:text-gray-100 dark:text-white dark:text-white">
            <ReactJson
              src={user}
              collapsed={true}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

interface UserDetailsDialogProps {
  readonly onCancel: () => void;
  readonly canMutate?: boolean;
  readonly user?: ClerkUser | null;
}

function UserMutationsForm(props: UserMutationsFormProps) {
  const metadata = props.user.publicMetadata || {};

  const queryClient = useQueryClient();

  const [
    isLoading,
    setLoading,
  ] = useState(false);
  const [
    error,
    setError,
  ] = useState<unknown>(null);
  const [
    success,
    setSuccess,
  ] = useState('');
  const [
    role,
    setRole,
  ] = useState(metadata.role || '');
  const [
    hubspotContactId,
    setHubspotContactId,
  ] = useState(metadata.hubspotContactId || '');
  const [
    banned,
    setBanned,
  ] = useState<boolean>(metadata.banned || false);
  const [
    locked,
    setLocked,
  ] = useState<boolean>(metadata.locked || false);

  useEffect(() => {
    if (!error && !success) {
      return;
    }

    setLoading(false);
  }, [
    error,
    success,
  ]);

  if (isLoading) {
    return <LoadingWidget />;
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-row gap-4 flex-wrap items-end">
        <div>
          <p>Rolle</p>
          <select
            value={role}
            onChange={(event) => setRole(event.target.value)}
          >
            <option value="user">User</option>
            <option value="partner">Partner (ACHTUNG)</option>
            <option value="internal">Internal (ACHTUNG)</option>
            <option value="admin">Admin (ACHTUNG)</option>
          </select>
        </div>
        <div>
          <p>HubSpot Contact ID</p>
          <input
            type="number"
            value={hubspotContactId}
            onChange={(event) =>
              setHubspotContactId(parseInt(event.target.value))
            }
          />
        </div>
        <div>
          <label>
            <input
              type="checkbox"
              checked={banned}
              onChange={(event) => setBanned(event.target.checked)}
            />{' '}
            Banned
          </label>
        </div>
        <div>
          <label>
            <input
              type="checkbox"
              checked={locked}
              onChange={(event) => setLocked(event.target.checked)}
            />{' '}
            Locked
          </label>
        </div>
        <div>
          <button
            className="bg-primary-600 hover:bg-primary-800 text-white p-2 rounded"
            onClick={async () => {
              try {
                setLoading(true);
                setError(null);
                setSuccess('');

                const rawBody = {
                  userId: props.user.id,
                  role: role,
                  hubspotContactId: hubspotContactId
                    ? parseInt(hubspotContactId)
                    : undefined,
                  banned: Boolean(banned),
                  locked: Boolean(locked),
                };

                const body =
                  ActionSchema[AppAction.UserMutation].safeParse(rawBody);

                if (!body.success) {
                  throw body.error;
                }

                const response = await fetchBackend(
                  `/action/${AppAction.UserMutation}`,
                  {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(body.data),
                  },
                );

                if (!response.ok) {
                  console.error(
                    `${response.status} ${response.statusText}`,
                    await response.text(),
                  );

                  throw new Error(`${response.status} ${response.statusText}`);
                }

                const result = await response.json();

                if (!result.success) {
                  console.error('Action failed', result);

                  throw new Error(JSON.stringify(result.errors));
                }

                setSuccess(
                  `Benutzer am ${formatDateTime(
                    new Date(),
                  )} erfolgreich angepasst.`,
                );

                await queryClient.invalidateQueries({
                  queryKey: [QueryReference.PageAdminUsers],
                });
              } catch (error) {
                setError(error);
              } finally {
                setLoading(false);
              }
            }}
          >
            Speichern
          </button>
        </div>
      </div>
      {error ? (
        <p className="text-red-900 dark:text-red-300 p-4 bg-red-50 dark:bg-red-950/30 rounded">
          {error.message || error || 'Empty ERROR!'}
        </p>
      ) : null}
      {success ? <p className="bg-green-300 p-4 rounded">{success}</p> : null}
    </div>
  );
}

interface UserMutationsFormProps {
  readonly user: ClerkUser;
}
